import { useState, useEffect } from 'react';
import { FolderSearch, FileText, Tags, BarChart3, RefreshCw, GitCompare, Copy, Shield, Layers } from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import ingestionService from './api';
import ProjectList from './components/ProjectList';
import DocumentBrowser from './components/DocumentBrowser';
import ClassifyView from './components/ClassifyView';
import SimilarityView from './components/SimilarityView';
import QualityAssessment from './components/QualityAssessment';
import DuplicatesView from './components/DuplicatesView';
import GovernanceView from './components/GovernanceView';

const DocIntelligence = () => {
  const [stats, setStats] = useState({ folders: 0, documents: 0, taxonomyNodes: 0 });
  const [activeTab, setActiveTab] = useState('scan');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [folders, docs, tax] = await Promise.all([
        ingestionService.listFolders().catch(() => []),
        ingestionService.listDocuments().catch(() => []),
        ingestionService.getTaxonomy().catch(() => []),
      ]);
      setStats({
        folders:       Array.isArray(folders) ? folders.length : 0,
        documents:     Array.isArray(docs) ? docs.length : 0,
        taxonomyNodes: Array.isArray(tax) ? tax.length : 0,
      });
    } catch {
      // ignore
    }
  };

  const tabs = [
    { id: 'scan',        label: 'Scan & Ingest',        icon: FolderSearch },
    { id: 'browse',      label: 'Documents & Taxonomy', icon: FileText },
    { id: 'classify',    label: 'Classify',             icon: Layers },
    { id: 'similarity',  label: 'Similarity',           icon: GitCompare },
    { id: 'quality',     label: 'Quality Assessment',   icon: BarChart3 },
    { id: 'duplicates',  label: 'Duplicates',           icon: Copy },
    { id: 'governance',  label: 'Governance',           icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-blue-50/10">
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Scanned Folders', value: stats.folders,       icon: FolderSearch, color: 'text-primary' },
            { label: 'Documents',       value: stats.documents,     icon: FileText,     color: 'text-primaryDark' },
            { label: 'Taxonomy Nodes',  value: stats.taxonomyNodes, icon: Tags,         color: 'text-primaryLight' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-4 flex items-center gap-4">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div>
                  <div className="text-xl font-bold text-gray-900">{s.value}</div>
                  <div className="text-sm text-gray-500">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div className="flex gap-3 items-end flex-wrap justify-end">
          <button
            onClick={loadStats}
            className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight"
          >
            <RefreshCw size={16} className="mr-1" />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div>
          <div className="flex gap-1 border-b border-gray-200 mb-6 whitespace-nowrap overflow-x-auto scrollbar-none">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px shrink-0 ${
                  activeTab === id
                    ? 'border-blue-600 text-primaryDark'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab(id)}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'scan'       && <ProjectList />}
          {activeTab === 'browse'     && <DocumentBrowser />}
          {activeTab === 'classify'   && <ClassifyView />}
          {activeTab === 'similarity' && <SimilarityView />}
          {activeTab === 'quality'    && <QualityAssessment />}
          {activeTab === 'duplicates' && <DuplicatesView />}
          {activeTab === 'governance' && <GovernanceView />}
        </div>
      </div>
    </div>
  );
};

export default DocIntelligence;
